import { LocationQueryRaw, RouteLocationRaw } from 'vue-router';

import { AppRouteNames } from './app-routes.enum';
import { PropertyOnboardingType } from './property-onboarding.enum';

export const signInRoute = (): RouteLocationRaw => {
  return { name: AppRouteNames.SIGN_IN };
};

export const signUpRoute = (): RouteLocationRaw => {
  return { name: AppRouteNames.SIGN_UP };
};

export const onboardingRoute = (): RouteLocationRaw => {
  return { name: AppRouteNames.INVESTOR_ONBOARDING };
};

export const emailConfirmationRoute = (queryParams?: LocationQueryRaw): RouteLocationRaw => {
  return {
    name: AppRouteNames.SIGN_UP_CONFIRM_EMAIL,
    query: queryParams || {},
  };
};

export const missingPasswordSignInRoute = (queryParams: { email: string }): RouteLocationRaw => {
  return {
    name: AppRouteNames.MISSING_PASSWORD_SIGN_IN,
    query: queryParams || {},
  };
};

export const resetPasswordRoute = (queryParams: { email: string; sent: string }): RouteLocationRaw => {
  return {
    name: AppRouteNames.RESET_PASSWORD,
    query: queryParams || {},
  };
};

export const confirmResetPasswordRoute = (): RouteLocationRaw => {
  return { name: AppRouteNames.RESET_PASSWORD_CONFIRM };
};

export const oneTimeCodeSignInRoute = (queryParams: { email: string }): RouteLocationRaw => {
  return {
    name: AppRouteNames.ONE_TIME_CODE_SIGN_IN,
    query: queryParams || {},
  };
};

export const showingsRoute = (): RouteLocationRaw => {
  return { name: AppRouteNames.INVESTOR_SHOWINGS };
};

export const showingDetailsRoute = (showingId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_SHOWING_DETAILS,
    params: { showingId },
  };
};

/** Which of the properties page's tabs to open; without one the page picks */
export type PortfolioTab = 'homes' | 'buildings';

/** The properties page, opened on one portfolio (`unassigned` for the units in none) or on everything */
export const portfolioRoute = (portfolio?: string, tab?: PortfolioTab): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PORTFOLIO,
    params: tab ? { tab } : {},
    query: portfolio ? { portfolio } : undefined,
  };
};

export const managePortfoliosRoute = (): RouteLocationRaw => {
  return { name: AppRouteNames.INVESTOR_PORTFOLIO_MANAGE };
};

export const profileRoute = (tab?: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROFILE,
    query: tab ? { tab } : undefined,
  };
};

/** The wizard for a single home, from its first screen; `query` carries the properties page's `?portfolio=` on */
export const newListingRoute = (query?: LocationQueryRaw): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_ONBOARDING_OVERVIEW,
    params: { propertyId: PropertyOnboardingType.NewProperty },
    query,
  };
};

export const newBuildingRoute = (query?: LocationQueryRaw): RouteLocationRaw => {
  return { name: AppRouteNames.INVESTOR_NEW_BUILDING, query };
};

export const buildingRoute = (buildingId: string): RouteLocationRaw => {
  return { name: AppRouteNames.INVESTOR_BUILDING, params: { buildingId } };
};

/** The building's details tab, where its own fields and its shared sections are edited */
export const buildingDetailsRoute = (buildingId: string): RouteLocationRaw => {
  return { name: AppRouteNames.INVESTOR_BUILDING_DETAILS, params: { buildingId } };
};

export const propertyRoute = (propertyId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY,
    params: { propertyId },
  };
};

export const propertyDashboardRoute = (propertyId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_DASHBOARD,
    params: { propertyId },
  };
};

export const propertyDetailsRoute = (propertyId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_DETAILS,
    params: { propertyId },
  };
};

export const propertyAvailabilityRoute = (propertyId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_AVAILABILITY,
    params: { propertyId },
  };
};

export const propertyTeamRoute = (propertyId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_TEAM,
    params: { propertyId },
  };
};

export const renterProfileRoute = (propertyId: string, renterId: string): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_RENTER_PROFILE,
    params: { renterId, propertyId },
  };
};

export const renterApplicationReviewRoute = (
  propertyId: string,
  renterId: string,
  applicationId: string,
): RouteLocationRaw => {
  return {
    name: AppRouteNames.INVESTOR_PROPERTY_RENTER_PROFILE_APPLICATION_REVIEW,
    params: { renterId, propertyId, applicationId },
  };
};

export const renterTransUnionReportRoute = (
  applicationId: string,
  reportType: string,
  format?: string,
): RouteLocationRaw => {
  return {
    name: AppRouteNames.APPLICATION_REPORT,
    params: { applicationId, reportType },
    ...(format && { query: { format } }),
  };
};
